import {Injectable} from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {OrganizationType} from '@portal/api/enums/organization-type';
import { OrganizationEntitlement } from '@portal/api/enums/organization-entitlement';
import {UsersOrganizationsPermissionsReadType} from '@portal/api/enums/users-organizations-permissions-read-type';
import {StorageService} from '@portal/shared/services/storage.service';
import {UserPermissionsService} from '@portal/shared/services/user-permissions.service';

@Injectable({
    providedIn: 'root'
})
export class AssociationPermissionsGuard  {
    constructor(
        private readonly _storageService: StorageService,
        private readonly _userPermissionsService: UserPermissionsService,
        private readonly _router: Router,
    ) {
    }

    canActivateChild(_childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        const org = this._storageService.getOrg();
        if (!org || org.type !== OrganizationType.Association) {
            return of(true);
        }

        const routeAccess = this.getRouteAccess(state.url);
        if (!routeAccess.permission && !routeAccess.entitlement) {
            return of(true);
        }

        if (this._userPermissionsService.currentPermissions) {
            return of(this.resolveRouteAccess(routeAccess));
        }

        return this._userPermissionsService.loadPermissions().pipe(
            map(() => this.resolveRouteAccess(routeAccess)),
            catchError(() => of(this.redirectHome()))
        );
    }

    private resolveRouteAccess(routeAccess: { permission: UsersOrganizationsPermissionsReadType | null; entitlement: OrganizationEntitlement | null }): boolean | UrlTree {
        const hasPermission = !routeAccess.permission
            || this._userPermissionsService.hasOrganizationReadPermissionValue(routeAccess.permission);
        const hasEntitlement = !routeAccess.entitlement
            || this._userPermissionsService.hasOrganizationEntitlement(routeAccess.entitlement);

        return hasPermission && hasEntitlement
            ? true
            : this.redirectHome();
    }

    private redirectHome(): UrlTree {
        return this._router.createUrlTree(['/organizations/dashboard']);
    }

    private getRouteAccess(url: string): { permission: UsersOrganizationsPermissionsReadType | null; entitlement: OrganizationEntitlement | null } {
        if (url.startsWith('/party/donations')) {
            return { permission: UsersOrganizationsPermissionsReadType.DonationRead, entitlement: OrganizationEntitlement.Donations };
        }
        if (url.startsWith('/party/sponsors')) {
            return { permission: UsersOrganizationsPermissionsReadType.SponsorsRead, entitlement: OrganizationEntitlement.Sponsors };
        }
        if (url.startsWith('/party/revenues')) {
            return { permission: UsersOrganizationsPermissionsReadType.RevenuesRead, entitlement: OrganizationEntitlement.Revenues };
        }
        if (url.startsWith('/organizations/club-members')) {
            return { permission: UsersOrganizationsPermissionsReadType.RevenuesRead, entitlement: OrganizationEntitlement.Revenues };
        }
        if (url.startsWith('/party/expenses')) {
            return { permission: UsersOrganizationsPermissionsReadType.ExpensesRead, entitlement: OrganizationEntitlement.Expenses };
        }
        if (url.startsWith('/party/bars')) {
            return { permission: UsersOrganizationsPermissionsReadType.BarsRead, entitlement: OrganizationEntitlement.Bars };
        }
        if (url.startsWith('/party/events')) {
            return { permission: UsersOrganizationsPermissionsReadType.EventsRead, entitlement: OrganizationEntitlement.Events };
        }
        if (url.startsWith('/party/inventory') || url.includes('/inventory')) {
            return { permission: UsersOrganizationsPermissionsReadType.EventsRead, entitlement: OrganizationEntitlement.Inventory };
        }
        if (url.startsWith('/party/merchandising') || url.includes('/merchandising')) {
            return { permission: UsersOrganizationsPermissionsReadType.EventsRead, entitlement: OrganizationEntitlement.Merchandising };
        }
        if (url.startsWith('/party/collaborators')) {
            return { permission: UsersOrganizationsPermissionsReadType.RevenuesRead, entitlement: OrganizationEntitlement.Collaborators };
        }
        if (url.startsWith('/organizations/users')) {
            return { permission: UsersOrganizationsPermissionsReadType.IsOwner, entitlement: null };
        }

        return { permission: null, entitlement: null };
    }
}
